import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Tabs } from "@shared/ui/tabs";
import { AlertCircle, ArrowLeft, MapPin, User, FileText, CheckCircle2, Navigation, Mail, CreditCard } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { cn } from "@shared/lib/utils";
import { ServicesMetersLanding } from "../../../profile/components/tabs/services-meters/ServicesMetersLanding";
import PlanAndDeposit from '../../../shared/components/PlanAndDeposit';
import { BillsTab } from './BillsTab';
import { ConsumptionTab } from './ConsumptionTab';
import { ComplaintsTab } from './ComplaintsTab';
import { ServiceRequestsTab } from './ServiceRequestsTab';
import { OtherRequestsTab } from './OtherRequestsTab';
import { AccountInfoTab } from './AccountInfoTab';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { useConsumerDetail } from '../../../shared/hook';
import { useTranslation } from 'react-i18next';
import { useCombinedBillsServices } from "../../../bills/hook";
import { useCurrency } from "@shared/selectors/globalSelectors";

export function Landing() {
  const { t } = useTranslation();
  const currency = useCurrency();
  const navigate = useNavigate();
  const { id: selectedConsumerId } = useParams();


  const { data: consumerDetails } = useConsumerDetail({
    remote_utility_id: getRemoteUtilityId(),
    consumer_id: selectedConsumerId

  });

  const consumerNo = consumerDetails?.result?.consumerNo;

  const { data: combinedBillsServicesData } = useCombinedBillsServices({
    remote_utility_id: String(getRemoteUtilityId()),
    consumer_number: consumerNo
  });

  const result = consumerDetails?.result || {};

  const firstName = result.firstName || '';
  const lastName = result.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim() || 'N/A';
  const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase() || 'NA';
  
  const category = result.categoryDisplay || 'N/A';
  const status = result.statusDisplay || 'N/A';
  
  const getAddressField = (addr: any, key: string) => {
    if (!addr) return '';
    return addr[key] || addr[key.toUpperCase()] || addr[key.toLowerCase()] || '';
  };

  const formatAddress = (addr: any) => {
    if (!addr) return 'N/A';
    const parts = [
      getAddressField(addr, 'address'),
      getAddressField(addr, 'unit'),
      getAddressField(addr, 'city'),
      getAddressField(addr, 'zipcode')
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };


  const serviceAddress = formatAddress(result.addressData?.service);
  const currentBalance = result.creditAvailable !== undefined ? `${currency} ${Number(result.creditAvailable).toFixed(2)}` : 'N/A';

  return (
    <div className="flex flex-col gap-6 p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <button 
          onClick={() => navigate('/dashboard/all-accounts')} 
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 w-fit"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("All Accounts")}
        </button>
        
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold text-lg tracking-wider shrink-0 mt-1">
              {initials}
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-1">
                <h1 className="text-2xl font-bold text-gray-900">{fullName}</h1>
                <Badge variant="secondary" className="bg-blue-50 text-blue-700 hover:bg-blue-50 border-0 font-medium px-2.5 py-0.5 text-xs rounded-full">
                  {category}
                </Badge>
                <Badge variant="secondary" className="bg-green-50 text-green-700 hover:bg-green-50 border-0 font-medium px-2.5 py-0.5 text-xs rounded-full">
                  {status}
                </Badge>
              </div>
              <p className="text-gray-500 text-sm mb-1">Acc: {consumerNo}</p>
              <div className="flex items-center gap-1.5 text-gray-500 text-sm">
                <MapPin className="w-3.5 h-3.5" />
                <span>{serviceAddress}</span>
              </div>
            </div>
          </div>
          <div className="md:text-right">
            <p className="text-gray-500 text-sm font-medium mb-1">{t("Outstanding")}</p>
            <p className="font-bold text-2xl text-red-600">{currentBalance}</p>
          </div>
        </div>
      </div>

      {/* Alert Bar */}
      <div className="bg-red-50/80 border border-red-100 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3 text-red-600">
          <AlertCircle className="w-5 h-5" />
          <span className="font-medium text-sm">
            {t("{{count}} unpaid bills — {{amount}} due", { 
              count: combinedBillsServicesData?.unpaidBillsCount || 0, 
              amount: `${currency} ${combinedBillsServicesData?.totalAmountDue || "0.00"}` 
            })}
          </span>
        </div>
        <Button variant='destructive' onClick={() => navigate(`/dashboard/billing/pay?type=bills&isAllAccounts=true&consumer_number=${consumerNo}`)}>
          <CreditCard className="w-4 h-4 mr-2" />
          {t("Pay Bills")}
        </Button>
      </div>

      {/* Tabs */}
      <Tabs
        defaultValue="account-info"
        className="w-full"
        tabsListClassName="grid grid-cols-8"
        tabsListFullWidth={true}
        tabComponents={{
          "account-info": {
            label: t("Account Info"),
            component: <AccountInfoTab />
          },
          "plan-deposit": {
            label: t("Plan & Deposit"),
            component: <PlanAndDeposit />
          },
          "services-meters": {
            label: t("Services & Meters"),
            count: 0,
            component: <ServicesMetersLanding />
          },
          "bills": {
            label: t("Bills & Services"),
            count: 0,
            component: <BillsTab consumerNo={consumerNo} />
          },
          "consumption": {
            label: t("Consumption"),
            component: <ConsumptionTab consumerNo={consumerNo} consumerId={selectedConsumerId} />
          },
          "complaints": {
            label: t("Complaints"),
            count: 0,
            component: <ComplaintsTab consumerInfo={{ name: fullName, accountNo: consumerNo, address: serviceAddress, selectedConsumerId }} />
          },
          "service-requests": {
            label: t("Service Requests"),
            component: <ServiceRequestsTab consumerInfo={{ name: fullName, accountNo: consumerNo, address: serviceAddress, selectedConsumerId }} />
          },
          "other-requests": {
            label: t("Other Requests"),
            count: 0,
            component: <OtherRequestsTab consumerInfo={{ name: fullName, accountNo: consumerNo, address: serviceAddress, selectedConsumerId }} />
          }
        }}
      />
    </div>
  );
}

export default Landing;
